import React, { useEffect, useMemo, useState } from "react";
import { LoadScript, Autocomplete, useJsApiLoader } from "@react-google-maps/api";

const libraries: "places"[] = ["places"];

interface GoogleAddressSearchProps {
    onSelect: (
        address: string,
        place: google.maps.places.PlaceResult,
        geo: {
            lat: number | null;
            lng: number | null;
        }
    ) => void;
    mode?: "city" | "address";
    countryCode?: string;
    value: string;
    className?: string;
    showManualToggle?: boolean;
    label?: React.ReactNode;
    onManualToggle?: (isManual: boolean) => void;
}

const GoogleAddressSearch: React.FC<GoogleAddressSearchProps> = ({
    onSelect,
    mode = "address",
    countryCode,
    value,
    className,
    showManualToggle = true,
    label,
    onManualToggle,
}) => {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
        libraries,
    });

    const [autocomplete, setAutocomplete] =
        useState<google.maps.places.Autocomplete | null>(null);
    const [str, setStr] = useState(value);
    const [isManual, setIsManual] = useState(false);

    const onLoad = (auto: google.maps.places.Autocomplete) => {
        if (mode === "city") auto.setTypes(["(cities)"]);
        if (countryCode) auto.setComponentRestrictions({ country: countryCode });
        setAutocomplete(auto);
    };

    useEffect(() => {
        setStr(value);
    }, [value]);

    const onPlaceChanged = () => {
        if (!autocomplete) return;

        const place = autocomplete.getPlace();
        let lat: number | null = null;
        let lng: number | null = null;
        if (place.geometry && place.geometry.location) {
            lat = place.geometry.location.lat();
            lng = place.geometry.location.lng();
        }
        const formatted = place.formatted_address || "";
        onSelect(formatted, place, { lat: lat, lng: lng });
    };

    const autoOptions = useMemo(() => ({
        types: mode === "city" ? ["(cities)"] : undefined,
        componentRestrictions: countryCode ? { country: countryCode } : undefined,
    }), [mode, countryCode]);

    return (
        <div className="w-100 position-relative">
            {(label || showManualToggle) && (
                <div className="d-flex justify-content-between align-items-center mb-1">
                    {label ? (
                        typeof label === "string" ? (
                            <label className="form-label mb-0">{label}</label>
                        ) : (
                            label
                        )
                    ) : (
                        <div />
                    )}
                    {showManualToggle && (
                        <span
                            role="button"
                            className="text-primary text-decoration-underline font-12 cursor-pointer ms-auto"
                            style={{ cursor: "pointer", fontSize: "12px", whiteSpace: "nowrap" }}
                            onClick={() => {
                                const nextManual = !isManual;
                                setIsManual(nextManual);
                                if (onManualToggle) {
                                    onManualToggle(nextManual);
                                }
                            }}
                        >
                            {isManual ? "Search with Google" : "Can't find address? Click here"}
                        </span>
                    )}
                </div>
            )}
            {isManual || !isLoaded ? (
                <input
                    type="text"
                    value={str}
                    onChange={(e) => {
                        setStr(e.target.value);
                        onSelect(e.target.value, {} as any, { lat: null, lng: null });
                    }}
                    placeholder={mode === "city" ? "Enter city manually" : "Enter address manually"}
                    style={{
                        width: "100%",
                        height: "44px",
                        padding: "8px",
                        border: "solid 1px #dee2e6 ",
                        borderRadius: "5px",
                    }}
                    className={className}
                />
            ) : (
                <Autocomplete
                    onLoad={onLoad}
                    onPlaceChanged={onPlaceChanged}
                    options={autoOptions}
                >
                    <input
                        type="text"
                        value={str}
                        onChange={(e) => {
                            setStr(e.target.value);
                            onSelect(e.target.value, {} as any, { lat: null, lng: null });
                        }}
                        placeholder={mode === "city" ? "Search & select your city" : "Search & select your address"}
                        style={{
                            width: "100%",
                            height: "44px",
                            padding: "8px",
                            border: "solid 1px #dee2e6 ",
                            borderRadius: "5px",
                        }}
                        className={className}
                    />
                </Autocomplete>
            )}
        </div>
    );
};

export default GoogleAddressSearch;
